import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, readInlineData, useFragment } from "react-relay";

import { checkIsSuperTopic } from "~/components/topic";
import type { contentTopicLinkContent$key } from "~/queries/__generated__/contentTopicLinkContent.graphql";
import type { contentTopicLinkSuperTopic$key } from "~/queries/__generated__/contentTopicLinkSuperTopic.graphql";

import { StyledTopicLink } from "./styles";

type Props = {
  reference: contentTopicLinkContent$key;
  variant: Variant;
};

type Variant =
  | { type: "main" }
  | { superTopicReference: contentTopicLinkSuperTopic$key; type: "sub-topic" };

export const ContentTopicLink: FunctionComponent<Props> = ({ reference: reference_, variant }) => {
  const content = useFragment(
    graphql`
      fragment contentTopicLinkContent on Content {
        topics {
          urlAlias
          ...entityLink
          ...topicLinkTopic
        }
      }
    `,
    reference_,
  );

  if (!content.topics) return null;

  switch (variant.type) {
    case "main": {
      const mainTopic = first(content.topics);
      if (!mainTopic) return null;
      return <StyledTopicLink reference={mainTopic} />;
    }
    case "sub-topic":
      const topic = readInlineData<contentTopicLinkSuperTopic$key>(
        graphql`
          fragment contentTopicLinkSuperTopic on Topic @inline {
            ...topicCheckSuperTopic
            urlAlias
            relatedLink {
              url
            }
          }
        `,
        variant.superTopicReference,
      );

      const isSuperTopic = checkIsSuperTopic(topic);
      const relatedLinks = new Set(
        topic.relatedLink
          ?.map(item => item?.url)
          .filter(element => notEmpty(element) && element !== topic.urlAlias),
      );
      if (!isSuperTopic || relatedLinks.size === 0) return null;

      const mainSubTopic = content.topics.find(
        item => item?.urlAlias && relatedLinks.has(item.urlAlias),
      );
      if (!mainSubTopic) return null;

      return <StyledTopicLink reference={mainSubTopic} />;
  }
};

ContentTopicLink.displayName = "ContentTopicLink";
